import { ref, computed } from '@vue/composition-api'
import Vue from 'vue'
import { utils, constants as c } from '@kingpin-global/kingpin-utils-frontend'
import {
  formatNumber,
  formatObject,
  downloadPDF,
  download,
  parseErrorObject,
} from '@/@core/utils/utils'
import {
  apiToastError,
  apiToastSuccess,
  apiToastWarning,
} from '@/@core/utils/toast'
import analytics from '@/@core/utils/analytics'
import { isRevertConfirmationNeeded } from '@/composables/useOrderPartialConfirm'
import constants, {
  DOWNLOAD_TYPES,
  FILE_DOWNLOAD_FORMATS,
  FILE_FORMATS,
  FILE_TYPES,
  FLAG_CONSTANTS,
} from '@/constants'
import store from '@/store'
import {
  CANCEL_ORDER,
  DOWNLOAD_ORDER,
  DOWNLOAD_ORDER_INVOICE,
  GET_ORDER,
  MODIFY_ORDER_PRODUCTS,
  ORDER_IMPORT,
  UPDATE_ORDER_PRODUCT_STATUS,
  REVERT_ORDER_TO_ACCEPTED,
  EXPORT_ORDER_FOR_MODIFY,
  IMPORT_ORDER_FOR_MODIFY,
} from '@/store/modules/order.module'
import { DOWNLOAD_SHIPMENT_INVOICE } from '@/store/modules/shipment.module'
import { DOWNLOAD_FILE, UPLOAD_DOCUMENTS } from '@/store/modules/common.module'
import { SHOW_DOWNLOADS_NAV_INDICATOR } from '@/store/modules/notification.module'
import { kpEndpoint, kpParams } from '@/axios'
import {
  enableShipmentFeature,
  formatProducts,
  getProductItem,
} from '@/views/apps/order/order-utils'
import { hasBeenChanged } from '@/views/apps/order/utils/hasBeenChanged'
import { isConfirmationStageOption } from '@/views/apps/order/order-confirmation-stages'
import { getOrderDownloadOptions } from '@/views/apps/order/order-download-options'

const { formattedDate, formatCurrency, getMonthAndYearFromDate } = utils

const {
  ORDER_STATUS,
  KP_DATE_FORMAT,
  ORDER_PRODUCT_SHIPMENT_STATUS,
  SHIPMENT_TEXT,
  BRAND_ACCESS,
} = c

const { DOWNLOAD_OPTIONS } = constants

export function useOrderDetail(shipmentOrderRef) {
  // State
  const isLoaded = ref(false)
  const order = ref(null)
  const orderData = ref(null)
  const totalUnits = ref(null)
  const total = ref(null)
  const isDownloading = ref(false)
  const isModifyEnabled = ref(false)
  const isInDraft = ref(false)
  const modifyUsingImport = ref(false)
  const reloadkey = ref(1)
  const filteredOrderProducts = ref([])
  const isProductsFilterApplied = ref(false)
  const cancelledProductIds = ref([])
  const isRevertingOrder = ref(false)
  const importedProductStatuses = ref([])
  const uploading = ref(false)
  const orderUploadFile = ref(null)
  const importForModifyFile = ref(null)
  const importingForModify = ref(false)
  const cancellingShipments = ref([])
  const downloadOption = ref({
    order: [
      FILE_DOWNLOAD_FORMATS.XLSX_STYLE,
      FILE_DOWNLOAD_FORMATS.XLSX_SKU,
    ],
  })

  // Store computeds
  const isBrand = computed(() => store.getters.isBrand)
  const isRetailer = computed(() => store.getters.isRetailer)
  const profileData = computed(() => store.state.auth.profileData)

  // Computeds
  const hasDuplicateOrderFeature = computed(() => store.state.flags[FLAG_CONSTANTS.DUPLICATE_ORDER])

  const orderId = computed(() => {
    if (!order.value) return ''
    const id = order.value.shipmentId || order.value._id
    return id.substring(id.length - 4)
  })

  const hasOrderConfirmed = computed(() => order.value?.status === ORDER_STATUS.CONFIRMED)

  const canShowDownloadOrderInvoiceBtn = computed(() => {
    if (!order.value) return false
    if (order.value.isSelfManagedOrder) {
      return order.value.shouldDisplayDownloadInvoice
    }
    const isKnownUserRole = isRetailer.value || isBrand.value
    const hasShipmentOrConfirmed = order.value.shipmentId || hasOrderConfirmed.value
    return isKnownUserRole && hasShipmentOrConfirmed
  })

  const isSelfManagedOrder = computed(() => order.value?.isSelfManagedOrder)

  const canShowDownloadOrderButton = computed(() => {
    if (!order.value) return false
    return (
      order.value.isDownloadEnabled
      || shipmentOrderRef?.value
      || order.value.shipmentId
    )
  })

  const isShipment = computed(() => {
    if (!order.value) return false
    return !!order.value.shipmentId || !!shipmentOrderRef?.value
  })

  const isDirectBrand = computed(() => (
    isBrand.value && profileData.value?.access?.includes(BRAND_ACCESS.DIRECT)
  ))

  const canShowBackToReceivedButton = computed(() => {
    if (!order.value) return false
    const isRevertableStatus = order.value.status === ORDER_STATUS.CONFIRMED
      || order.value.status === ORDER_STATUS.PENDING_RETAILER
      || order.value.status === constants.ORDER_STATUS.PARTIALLY_CONFIRMED
    const isNonShipmentBrand = isBrand.value && !shipmentOrderRef?.value
    return isRevertableStatus && isNonShipmentBrand
  })

  // Methods
  function mergeItems(newItems, currentItems) {
    if (!currentItems) {
      return newItems
    }
    return newItems.map(item => {
      const currentItem = currentItems.find(p => p._id === item._id)
      return {
        ...item,
        value: currentItem?.value ?? item.value,
      }
    })
  }

  function getMergedProducts(newProducts, currentProducts) {
    return newProducts.map(product => {
      const currentProduct = currentProducts.find(
        p => p._id === product._id,
      )
      if (!product.isDeletedByBrand && !product.isDeletedByRetailer) {
        const mergedItems = mergeItems(product.items, currentProduct?.items)
        return {
          ...product,
          items: mergedItems,
          quantity: currentProduct?.quantity ?? product.quantity,
        }
      }
      return product
    })
  }

  function getNonDeletedProducts(products) {
    return products.filter(product => !product.isDeletedByBrand && !product.isDeletedByRetailer)
  }

  function mergeOrderStates(newOrderData) {
    if (!order.value) {
      return newOrderData
    }
    const currentOrderData = { ...order.value }
    isInDraft.value = hasBeenChanged(
      getNonDeletedProducts(newOrderData.products),
      getNonDeletedProducts(currentOrderData.products),
    )
    if (!isInDraft.value) {
      isModifyEnabled.value = false
      modifyUsingImport.value = false
      return newOrderData
    }
    const updatedProducts = getMergedProducts(
      newOrderData.products,
      currentOrderData.products,
    )
    return {
      ...newOrderData,
      products: updatedProducts,
    }
  }

  function setDates(orderObj) {
    if (orderObj.expectedAvailabilityDate && !orderObj.isDataForCreateShipment) {
      order.value.expectedAvailabilityDate = getMonthAndYearFromDate(
        orderObj.expectedAvailabilityDate,
      )
    }
  }

  function loadOrderData(newOrderData) {
    order.value = mergeOrderStates(newOrderData)
    orderData.value = JSON.parse(JSON.stringify(newOrderData))
    totalUnits.value = orderData.value.totalQuantity
    isLoaded.value = true
    setDates(newOrderData)
  }

  function getOrderPageTrack() {
    return isBrand.value
      ? constants.TRACKS.PAGES.BRAND_OPENS_ORDER_DETAILS
      : constants.TRACKS.PAGES.RETAILER_OPENS_ORDER_DETAILS
  }

  function handleOrderFetchSuccess(res) {
    const data = res.data.data
    enableShipmentFeature(data)
    loadOrderData(data)
    analytics.track(getOrderPageTrack(), { 'Order Id': data.orderSeqId })
  }

  function loadOrder(routeOrderId) {
    isLoaded.value = false

    if (shipmentOrderRef?.value) {
      loadOrderData(shipmentOrderRef.value)
      return Promise.resolve()
    }

    if (!routeOrderId) {
      isLoaded.value = true
      return Promise.resolve()
    }

    return store
      .dispatch(GET_ORDER, routeOrderId)
      .then(handleOrderFetchSuccess)
      .catch(() => { })
      .finally(() => {
        isLoaded.value = true
      })
  }

  function initDownloadOptions() {
    downloadOption.value = {
      order: getOrderDownloadOptions({
        order: order.value,
        isShipment: isShipment.value,
        isShipmentOrder: !!shipmentOrderRef?.value,
        canDownloadInvoice: canShowDownloadOrderInvoiceBtn.value,
      }),
    }
  }

  function updateOrder(shouldConfirmOrder = false) {
    const { payload, cancelledProductsCount } = formatProducts(
      order.value.products,
      importedProductStatuses.value,
    )
    const params = {}
    if (shouldConfirmOrder) {
      params.shouldConfirmOrder = shouldConfirmOrder
    }
    const orderId = order.value._id
    return store
      .dispatch(MODIFY_ORDER_PRODUCTS, { orderId, params, payload })
      .then(res => {
        apiToastSuccess(res.data.message || 'Updated this order!')
        if (isBrand.value) {
          analytics.track(
            constants.TRACKS.ACTIONS.BRAND_MODIFIES_ORDER,
            formatObject({
              orderId: order.value.orderSeqId,
              collectionName: order.value.collectionName,
              noOfCancelledProducts: cancelledProductsCount,
              sumOfRemovedQuantities:
                order.value.totalQuantity - totalUnits.value,
            }),
          )
        }
        // Refresh server-side state so the status badge, available
        // action buttons, and any other order-derived computed props
        // re-evaluate. Without this, after a successful Modify or
        // Modify & Confirm the UI keeps showing the pre-action state
        // (e.g. status stays Pending Confirmation even though BE has
        // moved to Confirmed). Mirrors the saveOrder pattern in
        // useOrderActions.js.
        return loadOrder(orderId)
          .then(() => {
            // Exit inline-modify mode so the row-level quantity editors
            // close and the "Modify Order" entry point reappears.
            isModifyEnabled.value = false
            return { success: true }
          })
      })
      .catch(err => ({ success: false, err }))
  }

  function removeOrderProduct(product, index, routeOrderId) {
    const trackName = isBrand.value ? constants.TRACKS.ACTIONS.SINGLE_ORDER.REMOVE_ORDER_PRODUCT : constants.TRACKS.ACTIONS.SINGLE_ORDER.RETAILER_REMOVE_ORDER_PRODUCT
    analytics.track(trackName, {
      orderId: order.value._id,
      collectionId: order.value.collectionId,
      product,
    })
    const payload = {
      productIds: [product.productId],
      status: ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL,
    }
    return store
      .dispatch(UPDATE_ORDER_PRODUCT_STATUS, {
        orderId: order.value._id,
        payload,
      })
      .then(res => {
        order.value.products[index].isDeletedByBrand = true
        if (isRetailer.value) {
          order.value.products[index].isDeletedByRetailer = true
        }
        cancelledProductIds.value.push({
          id: product.productId,
          index,
        })
        loadOrder(routeOrderId)
        apiToastSuccess(res.data.message || 'Removed product from this order!')
      })
      .catch(() => { })
  }

  async function restoreDeletedProducts() {
    analytics.track(
      constants.TRACKS.ACTIONS.SINGLE_ORDER.REVERT_DELETED_PRODUCTS,
      {
        orderId: order.value._id,
        collectionId: order.value.collectionId,
        productsToRevert: cancelledProductIds.value,
      },
    )
    const payload = {
      productIds: cancelledProductIds.value.map(product => product.id),
      status: ORDER_PRODUCT_SHIPMENT_STATUS.IN_PROCESS.LABEL,
      isReverted: true,
    }
    try {
      await store.dispatch(UPDATE_ORDER_PRODUCT_STATUS, {
        orderId: order.value._id,
        payload,
      })
      cancelledProductIds.value = []
    } catch {
      // error toast handled by axios interceptor
    }
  }

  function enableModifyProducts() {
    isModifyEnabled.value = true
    order.value.products.forEach(product => {
      product.isEdited = false
    })
  }

  async function revertModifiedChanges(routeOrderId) {
    analytics.track(
      constants.TRACKS.ACTIONS.SINGLE_ORDER.REVERT_ORDER_EDIT,
      {
        orderId: order.value._id,
        collectionId: order.value.collectionId,
        orderData: order.value,
      },
    )
    order.value.products.forEach((product, index) => {
      const original = orderData.value.products[index]
      product.items = getProductItem(original.items)
      if (product.isCarton) {
        product.quantity = original.quantity
      }
      product.isEdited = original.isEdited
      Vue.set(product, 'status', original.status)
      Vue.set(product, 'isDeletedByBrand', original.isDeletedByBrand ?? false)
      Vue.set(product, 'isDeletedByRetailer', original.isDeletedByRetailer ?? false)
    })
    if (cancelledProductIds.value.length > 0) {
      await restoreDeletedProducts()
      loadOrder(routeOrderId)
    }
    modifyUsingImport.value = false
    isModifyEnabled.value = false
    importedProductStatuses.value = []
    totalUnits.value = orderData.value.totalQuantity
    reloadkey.value++
  }

  function onPrintOrder() {
    isDownloading.value = true
    let payload = {}
    if (isShipment.value) {
      payload = {
        url: kpParams(kpEndpoint.shipment.printShipmentDetail, order.value._id),
        fileName: `shipment-detail-${order.value.shipmentSeqId}.pdf`,
        successMessage: `Your shipment-detail-${order.value.shipmentSeqId} is being downloaded...`,
        fileFormat: FILE_FORMATS.PDF,
      }
    } else {
      payload = {
        url: kpParams(kpEndpoint.order.printOrderDetail, order.value._id),
        fileName: `order-detail-${order.value.orderSeqId}.pdf`,
        successMessage: `Your order-detail-${order.value.orderSeqId} is being downloaded...`,
        fileFormat: FILE_FORMATS.PDF,
      }
    }
    return store
      .dispatch(DOWNLOAD_FILE, payload)
      .catch(err => {
        apiToastError(err)
      })
      .finally(() => {
        isDownloading.value = false
      })
  }

  function downloadConfirmationStagePdf(confirmationSeqId) {
    isDownloading.value = true
    const fileName = `order-confirmation-${confirmationSeqId}`
    return store
      .dispatch(DOWNLOAD_FILE, {
        url: kpParams(
          kpEndpoint.order.printConfirmationDetail,
          order.value._id,
          encodeURIComponent(confirmationSeqId),
        ),
        fileName: `${fileName}.pdf`,
        successMessage: `Your ${fileName} is being downloaded...`,
        fileFormat: FILE_FORMATS.PDF,
      })
      .catch(err => {
        apiToastError(err)
      })
      .finally(() => {
        isDownloading.value = false
      })
  }

  function downloadExcelFile(type) {
    if (isShipment.value) {
      return downloadShipmentExcelFile(type)
    }
    const params = { type }
    const payload = {
      downloadType: DOWNLOAD_TYPES.ORDER_EXPORT.VALUE,
      respectiveId: order.value._id,
      fileType: FILE_FORMATS.XLSX,
    }
    isDownloading.value = true
    return store
      .dispatch(DOWNLOAD_ORDER, { payload, params })
      .then(res => {
        apiToastSuccess(res.data.message)
        store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
      })
      .catch(err => {
        apiToastError(err)
      })
      .finally(() => {
        isDownloading.value = false
      })
  }

  function downloadShipmentExcelFile(type) {
    isDownloading.value = true
    const params = { type }
    const payload = {
      url: kpParams(kpEndpoint.shipment.downloadProducts, order.value.shipmentId),
      fileName: `Shipment-Products-${order.value.shipmentSeqId}.xlsx`,
      successMessage: `Shipment-Products-${order.value.shipmentSeqId}.xlsx`,
      fileFormat: FILE_FORMATS.XLSX,
      params,
    }
    return store
      .dispatch(DOWNLOAD_FILE, payload)
      .catch(err => {
        apiToastError(err)
      })
      .finally(() => {
        isDownloading.value = false
      })
  }

  function downloadZipFile() {
    isDownloading.value = true
    const payload = {
      downloadType: DOWNLOAD_TYPES.ORDER_EXPORT.VALUE,
      respectiveId: order.value._id,
      fileType: FILE_FORMATS.ZIP,
    }
    return store
      .dispatch(DOWNLOAD_ORDER, { payload })
      .then(res => {
        apiToastSuccess(res.data.message)
        analytics.track(
          constants.TRACKS.ACTIONS.DOWNLOAD_PRODUCT_IMAGES_ORDER,
          formatObject(order.value),
        )
        store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
      })
      .catch(err => {
        apiToastError(err)
      })
      .finally(() => {
        isDownloading.value = false
      })
  }

  function getInvoiceLabel() {
    return isShipment.value ? 'shipment' : 'order'
  }

  function getInvoiceSeqId() {
    return isShipment.value
      ? order.value.shipmentSeqId
      : order.value.orderSeqId
  }

  function downloadInvoiceFromUrl(invoiceUrl, fileName, seqId) {
    download(invoiceUrl, fileName)
    apiToastSuccess(
      `Your Invoice is downloaded for the ${getInvoiceLabel()} ${seqId}`,
    )
  }

  function downloadInvoiceFromBlob(blobData, seqId) {
    const reader = new FileReader()
    reader.readAsDataURL(blobData)
    reader.onloadend = () => {
      downloadPDF({
        pdf: reader.result,
        fileName: `${seqId}-Invoice`,
      })
    }
  }

  function trackInvoiceDownload() {
    const trackSubject = isRetailer.value
      ? constants.TRACKS.ACTIONS.RETAILER_DOWNLOADS_INVOICE
      : constants.TRACKS.ACTIONS.BRAND_DOWNLOADS_INVOICE
    analytics.track(trackSubject)
  }

  function handleInvoiceResponse(res) {
    const seqId = getInvoiceSeqId()
    const hasDirectInvoice = order.value.shouldDisplayDownloadInvoice
      && order.value.invoiceUrl
      && !isShipment.value

    if (hasDirectInvoice) {
      downloadInvoiceFromUrl(order.value.invoiceUrl, `order-invoice-${order.value.orderSeqId}`, seqId)
      trackInvoiceDownload()
      return
    }

    if (res.data.invoiceUrl) {
      downloadInvoiceFromUrl(res.data.invoiceUrl, `${seqId}-Invoice`, seqId)
      trackInvoiceDownload()
      return
    }

    if (order.value.isSelfManaged) {
      apiToastWarning(res.message)
      return
    }

    downloadInvoiceFromBlob(res.data, seqId)
    apiToastSuccess(
      `Your Invoice is downloaded for the ${getInvoiceLabel()} ${seqId}`,
    )
    trackInvoiceDownload()
  }

  function downloadOrderInvoice() {
    const endPoint = isShipment.value
      ? DOWNLOAD_SHIPMENT_INVOICE
      : DOWNLOAD_ORDER_INVOICE

    return store
      .dispatch(endPoint, order.value._id)
      .then(res => {
        handleInvoiceResponse(res)
      })
      .catch(err => {
        console.error(err)
        apiToastWarning('Invoice not available for this shipment, please contact brand')
      })
      .finally(() => {
        isDownloading.value = false
      })
  }

  function startDownload(downloadType) {
    if (isConfirmationStageOption(downloadType)) {
      downloadConfirmationStagePdf(downloadType.confirmationSeqId)
      return
    }
    const handlers = {
      [FILE_DOWNLOAD_FORMATS.XLSX_STYLE.value]: () => downloadExcelFile(downloadType.value),
      [FILE_DOWNLOAD_FORMATS.XLSX_SKU.value]: () => downloadExcelFile(downloadType.value),
      [FILE_DOWNLOAD_FORMATS.ORDER_DETAILS_PDF.value]: onPrintOrder,
      [FILE_DOWNLOAD_FORMATS.SHIPMENT_DETAILS_PDF.value]: onPrintOrder,
      [FILE_DOWNLOAD_FORMATS.ZIP.value]: downloadZipFile,
      [FILE_DOWNLOAD_FORMATS.PDF.value]: downloadOrderInvoice,
    }
    const handler = handlers[downloadType.value]
    if (handler) handler()
  }

  function downloadOrderProducts(downloadType) {
    isDownloading.value = true
    startDownload(downloadType)
    analytics.track(
      isBrand.value
        ? constants.TRACKS.ACTIONS.BRAND_DOWNLOADS_ORDER
        : constants.TRACKS.ACTIONS.RETAILER_DOWNLOADS_ORDER,
      formatObject({
        orderId: order.value.orderSeqId,
        exportFileFormat: downloadType,
      }),
    )
  }

  function onAppliedFilter(isFilterAppliedVal = false, products = []) {
    filteredOrderProducts.value = products
    isProductsFilterApplied.value = isFilterAppliedVal
  }

  function cancelOrderByBrand(isConfirmed, routeOrderId) {
    const cancelOrder = params => {
      store
        .dispatch(CANCEL_ORDER, params)
        .then(res => {
          apiToastSuccess(res.data.message || 'Order cancelled successfully')
          loadOrder(routeOrderId)
        })
        .catch(err => {
          if (err.response?.status === 425) {
            cancellingShipments.value = err.response.data.data.cancellingShipments
            return { showCancelModal: true }
          }
          return { showCancelModal: false }
        })
    }

    if (!isConfirmed) {
      return cancelOrder({ orderId: routeOrderId })
    }
    return cancelOrder({
      orderId: routeOrderId,
      params: { shouldProceed: true },
    })
  }

  // A confirmed or partially confirmed order asks first; anything else reverts straight away.
  async function handleRevertToAccepted(routeOrderId) {
    if (isRevertConfirmationNeeded(order.value)) {
      return { showRevertModal: true }
    }
    await revertOrderToAccepted(routeOrderId)
    return { showRevertModal: false }
  }

  function revertOrderToAccepted(routeOrderId) {
    isRevertingOrder.value = true
    return store
      .dispatch(REVERT_ORDER_TO_ACCEPTED, routeOrderId)
      .then(res => {
        apiToastSuccess(res.data.message || 'Order reverted to received status!')
        loadOrder(routeOrderId)
      })
      .catch(() => { })
      .finally(() => {
        isRevertingOrder.value = false
      })
  }

  async function exportOrderForModify() {
    try {
      const payload = {
        downloadType: 'orderProductsExport',
        fileType: FILE_FORMATS.XLSX,
        respectiveId: order.value._id,
      }
      const response = await store.dispatch(EXPORT_ORDER_FOR_MODIFY, { payload })
      apiToastSuccess(response.data.message)
      store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
    } catch (err) {
      apiToastError(err)
    }
  }

  function syncOrderProductItemsFromOrderData() {
    order.value.products.forEach((product, index) => {
      product.items = getProductItem(orderData.value.products[index].items)
    })
  }

  function applyImportedProductQuantity(product, index) {
    const orderProduct = order.value.products[index]
    if (orderProduct.isCarton) {
      orderProduct.quantity = product.quantity
    } else {
      orderProduct.items = getProductItem(
        orderData.value.products[index].items,
        product.items,
      )
    }
  }

  function applyImportedProductStatus(product, index) {
    if (!product.status) return
    const originalStatus = orderData.value.products[index].status
    if (product.status !== originalStatus) {
      importedProductStatuses.value.push({
        productId: product.productId,
        status: product.status,
      })
    }
    Vue.set(order.value.products[index], 'status', product.status)
    if (originalStatus === 'cancelled' && product.status !== 'cancelled') {
      Vue.set(order.value.products[index], 'isDeletedByBrand', false)
      Vue.set(order.value.products[index], 'isDeletedByRetailer', false)
    }
  }

  function applyImportedProduct(product) {
    if (!product.productId) return
    const index = order.value.products.findIndex(
      p => p.productId === product.productId,
    )
    if (index < 0) return
    applyImportedProductQuantity(product, index)
    applyImportedProductStatus(product, index)
  }

  function importedProducts(products) {
    modifyUsingImport.value = true
    importedProductStatuses.value = []
    syncOrderProductItemsFromOrderData()
    products.forEach(product => applyImportedProduct(product))
    reloadkey.value++
  }

  async function handleImportForModify() {
    if (!importForModifyFile.value) {
      apiToastWarning('Please select a file')
      return { success: false }
    }
    importingForModify.value = true
    try {
      const fileUrl = await store.dispatch(UPLOAD_DOCUMENTS, {
        fileType: FILE_TYPES.ORDER_UPLOAD,
        file: importForModifyFile.value,
      })
      const payload = { fileUrl }
      const result = await store.dispatch(IMPORT_ORDER_FOR_MODIFY, {
        orderId: order.value._id,
        payload,
      })
      importForModifyFile.value = null
      apiToastSuccess(result.data.message || 'Order imported successfully')
      importedProducts(result.data.data.products || result.data.data)
      return { success: true }
    } catch (err) {
      const message = parseErrorObject(err) || 'Failed to import products. Please check the file format and try again.'
      apiToastError(message)
      return { success: false }
    } finally {
      importingForModify.value = false
    }
  }

  async function syncOrderProducts() {
    if (orderUploadFile.value) {
      uploading.value = true
      try {
        const fileLink = await store.dispatch(UPLOAD_DOCUMENTS, {
          fileType: FILE_TYPES.ORDER_UPLOAD,
          file: orderUploadFile.value,
        })
        const payload = {
          orderProductsFile: fileLink,
        }
        const result = await store.dispatch(ORDER_IMPORT, { orderId: order.value._id, payload })
        orderUploadFile.value = null
        apiToastSuccess(result.data.message)
        importedProducts(result.data.data)
        uploading.value = false
        return { success: true }
      } catch {
        uploading.value = false
        return { success: false }
      }
    } else {
      apiToastWarning('Please Select a File')
      return { success: false }
    }
  }

  return {
    // State
    isLoaded,
    order,
    orderData,
    totalUnits,
    total,
    isDownloading,
    isModifyEnabled,
    isInDraft,
    modifyUsingImport,
    reloadkey,
    filteredOrderProducts,
    isProductsFilterApplied,
    cancelledProductIds,
    isRevertingOrder,
    importedProductStatuses,
    uploading,
    orderUploadFile,
    importForModifyFile,
    importingForModify,
    downloadOption,
    cancellingShipments,

    // Computeds
    isBrand,
    isRetailer,
    profileData,
    hasDuplicateOrderFeature,
    orderId,
    hasOrderConfirmed,
    canShowDownloadOrderInvoiceBtn,
    isSelfManagedOrder,
    canShowDownloadOrderButton,
    isShipment,
    isDirectBrand,
    canShowBackToReceivedButton,

    // Methods
    loadOrder,
    loadOrderData,
    mergeOrderStates,
    initDownloadOptions,
    updateOrder,
    removeOrderProduct,
    restoreDeletedProducts,
    enableModifyProducts,
    revertModifiedChanges,
    downloadOrderProducts,
    downloadExcelFile,
    downloadShipmentExcelFile,
    downloadZipFile,
    downloadOrderInvoice,
    onPrintOrder,
    cancelOrderByBrand,
    handleRevertToAccepted,
    revertOrderToAccepted,
    exportOrderForModify,
    handleImportForModify,
    syncOrderProducts,
    importedProducts,
    onAppliedFilter,
    setDates,

    // Constants
    ORDER_STATUS,
    SHIPMENT_TEXT,
    DOWNLOAD_OPTIONS,
    FILE_DOWNLOAD_FORMATS,
    KP_DATE_FORMAT,
    formatNumber,
    formattedDate,
    formatCurrency,
  }
}
